import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { Flex } from 'reflexbox/styled-components'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'

import TextButton from 'components/TextButton'
import Button from 'components/Button'
import DataListingTable from 'components/DataListingTable'
import { SearchInput, SelectField, DateRangePickerField } from 'components/Form'
import ToggleBeneficiaryStatusModal from 'components/Modal/ActivateModal'
import RemoveBeneficaryFromGroupModal from 'components/Modal/DeleteModal'

import { ActivateIcon, DeactivateIcon } from './styles'

import { DEFAULT_PAGE_SIZE } from 'constants/pagination'
import localStorage from 'helpers/localStorage'
import useDebounce from 'hooks/debounce'
import beneficiariesService from 'services/beneficiaries'
import beneficiaryGroupService from 'services/beneficiaryGroups'

const BeneficiariesDataListing = ({ groupID }) => {
  const [beneficiaries, setBeneficiaries] = useState([])

  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: localStorage.get('pageSize', DEFAULT_PAGE_SIZE),
    totalPages: 1,
    totalRecords: 0,
  })
  const [searchTerm, setSearchTerm] = useState(null)
  const [filters, setFilters] = useState({})
  const [isLoading, setLoading] = useState(true)

  const [beneficiaryToToggleStatus, setBeneficiaryToToggleStatus] = useState(null)
  const [isToggleBeneficiaryStatusModalOpen, setToggleBeneficiaryStatusModalOpen] = useState(false)
  const [beneficiaryToRemoveFromGroup, setBeneficiaryToRemoveFromGroup] = useState(null)
  const [isRemoveBeneficiaryFromGroupModalOpen, setRemoveBeneficiaryFromGroupModalOpen] = useState(false)
  const [isRemovingBeneficiaryFromGroup, setRemovingBeneficiaryFromGroup] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: 'Nome',
        accessor: 'name',
      },
      {
        Header: 'CPF / CNPJ',
        accessor: 'document',
      },
      {
        Header: 'E-mail',
        accessor: 'email',
      },
      {
        Header: 'Dia de cobrança',
        accessor: 'activation_day',
        id: 'activation_date__day',
      },
      {
        Header: 'Grupos',
        accessor: 'groups',
        id: 'groups_count',
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
      },
      {
        Header: '',
        accessor: 'actions',
        disableSortBy: true,
      },
    ],
    [],
  )

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const handleToggleBeneficiaryStatusActionButtons = beneficiary => {
    setBeneficiaryToToggleStatus(beneficiary)
    setToggleBeneficiaryStatusModalOpen(true)
  }

  const handleToggleBeneficiaryModalOpenState = isOpen => {
    setToggleBeneficiaryStatusModalOpen(isOpen)

    if (!isOpen) {
      fetchBeneficiaries()
    }
  }

  const handleRemoveBeneficiaryFromGroupActionButton = beneficiary => {
    setBeneficiaryToRemoveFromGroup(beneficiary)
    setRemoveBeneficiaryFromGroupModalOpen(true)
  }

  const handleRemoveBeneficiaryFromGroup = async () => {
    setRemovingBeneficiaryFromGroup(true)

    const { id: beneficiaryID, name } = beneficiaryToRemoveFromGroup

    try {
      await beneficiaryGroupService.removeBeneficiary({ groupID, beneficiaryID })

      toast.success(`Beneficiário ${name} removido do grupo com sucesso`)
    } catch (e) {
      console.log(e)
      toast.error(`Não foi possível remover o beneficiário ${name} do grupo`)
    } finally {
      setRemoveBeneficiaryFromGroupModalOpen(false)
      setRemovingBeneficiaryFromGroup(false)
      fetchBeneficiaries()
    }
  }
  const handleSubmitFilterForm = ({ status, date: { startDate, endDate } }) =>
    setFilters({
      status: status?.value,
      start_date: startDate?.format('YYYYMMDD'),
      end_date: endDate?.format('YYYYMMDD'),
    })

  const fetchBeneficiaries = async () => {
    setLoading(true)

    try {
      const response = await beneficiariesService.fetchAll({
        sortBy,
        pagination,
        groupID,
        search: debouncedSearchTerm,
        filters,
      })

      const beneficiaries = response.beneficiaries.map(beneficiary => ({
        ...beneficiary,
        id: <Link to={`/prepaid-purchase/beneficiaries/${beneficiary.id}`}>{beneficiary.id}</Link>,
        actions: renderTableRowActions(beneficiary),
      }))

      setBeneficiaries(beneficiaries)
      setPagination(response.pagination)
    } catch (e) {
      console.log(e)
      toast.error('Ocorreu algum erro no carregamento dos dados')
    } finally {
      setLoading(false)
    }
  }

  const renderTableRowActions = beneficiary => (
    <>
      {beneficiary.is_active ? (
        <TextButton
          title='Desativar Beneficiário'
          variant='tertiary'
          onClick={() => handleToggleBeneficiaryStatusActionButtons(beneficiary)}
        >
          <DeactivateIcon />
          Inativar
        </TextButton>
      ) : (
        <TextButton title='Ativar Beneficiário' onClick={() => handleToggleBeneficiaryStatusActionButtons(beneficiary)}>
          <ActivateIcon />
          Ativar
        </TextButton>
      )}
      {groupID && (
        <TextButton
          ml={2}
          variant='tertiary'
          title='Remover do Grupo'
          onClick={() => handleRemoveBeneficiaryFromGroupActionButton(beneficiary)}
        >
          <DeactivateIcon />
          Remover
        </TextButton>
      )}
    </>
  )

  useEffect(() => {
    if (sortBy) {
      fetchBeneficiaries()
    }
  }, [sortBy, pagination.currentPage, pagination.pageSize, filters])

  useEffect(() => {
    if (pagination.currentPage === 1 && debouncedSearchTerm) {
      fetchBeneficiaries()
    }
    setPagination({ ...pagination, currentPage: 1 })
  }, [debouncedSearchTerm])

  useEffect(() => {
    localStorage.set('pageSize', pagination.pageSize)
  }, [pagination.pageSize])

  return (
    <>
      <Flex flexDirection='column'>
        <Flex alignItems='center' justifyContent='flex-start' mb={24}>
          <Formik
            initialValues={{ search: '', status: null, date: { startDate: undefined, endDate: undefined } }}
            onSubmit={handleSubmitFilterForm}
          >
            <Flex as={Form}>
              <SearchInput
                name='search'
                label='Buscar'
                placeholder='Buscar'
                onChange={e => setSearchTerm(e.target.value)}
                mr={24}
              />
              <DateRangePickerField mr={24} name='date' label='Período' />
              <SelectField
                mr={24}
                minWidth={180}
                name='status'
                label='Status'
                options={[
                  { label: 'Ativo', value: 'active' },
                  { label: 'A Ativar', value: 'waiting' },
                  { label: 'Inativo', value: 'inactive' },
                ]}
              />
              <Button type='submit' variant='secondary'>
                Filtrar
              </Button>
            </Flex>
          </Formik>
        </Flex>
        <DataListingTable
          data={beneficiaries}
          columns={columns}
          pagination={pagination}
          loading={isLoading}
          fetchData={handleTableDataFetch}
        />
      </Flex>

      <ToggleBeneficiaryStatusModal
        isOpened={isToggleBeneficiaryStatusModalOpen}
        setIsOpened={handleToggleBeneficiaryModalOpenState}
        beneficiary={beneficiaryToToggleStatus}
      />

      <RemoveBeneficaryFromGroupModal
        isOpened={isRemoveBeneficiaryFromGroupModalOpen}
        setIsOpened={setRemoveBeneficiaryFromGroupModalOpen}
        title={`Remover beneficiário ${beneficiaryToRemoveFromGroup?.name}`}
        text='Tem certeza que deseja remover este beneficiário do grupo?'
        onDelete={handleRemoveBeneficiaryFromGroup}
        status={isRemovingBeneficiaryFromGroup}
      />
    </>
  )
}

BeneficiariesDataListing.propTypes = {
  groupID: PropTypes.number,
}

export default BeneficiariesDataListing
